import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { tl, stat } from "@/lib/i18nFormat";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { PageHeader, StatusPill, EmptyState, LoadingBlock } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { TrendingDown, Megaphone, CheckCircle2, RotateCcw, Ban, Eye } from "lucide-react";

const STATUS_TONE: Record<string, string> = {
  published: "good", submitted: "warn", draft: "inactive", superseded: "inactive",
  cancelled: "critical", expired: "inactive", rejected: "critical",
};

const ForecastAdjustments: React.FC = () => {
  const { t } = useI18n();
  const { hasPerm } = useAuth();
  const canApprove = hasPerm("goal.approve");
  const canReport = hasPerm("goal.propose");
  const [adjustments, setAdjustments] = useState<any[] | null>(null);
  const [reports, setReports] = useState<any[]>([]);
  const [stores, setStores] = useState<any[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [preview, setPreview] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState<any>({ level: "store", ref_id: "", metric: "traffic", method: "percent", value: "-20", reason_category: "tech_outage", explanation: "", effective_from: "", effective_to: "", exceptional_ack: false });
  const [rform, setRform] = useState<any>({ store_id: "", category: "tech_outage", description: "", metric_hint: "traffic" });

  const load = useCallback(() => {
    api.get("/forecast-adjustments").then((r) => setAdjustments(r.data.adjustments)).catch(() => setAdjustments([]));
    if (canReport) api.get("/forecast-adjustments/reports").then((r) => setReports(r.data.reports)).catch(() => setReports([]));
    api.get("/org/stores").then((r) => setStores(r.data.stores || r.data || [])).catch(() => setStores([]));
  }, [canReport]);
  useEffect(() => { load(); }, [load]);

  const submitReport = async () => {
    if (!rform.store_id || !rform.description.trim()) { toast.error("Store and description required"); return; }
    setBusy(true);
    try { await api.post("/forecast-adjustments/reports", rform); toast.success("Exception reported"); setShowReport(false); load(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const createAdj = async () => {
    if (!form.exceptional_ack) { toast.error(t("g3.exceptional_ack")); return; }
    setBusy(true);
    try {
      const body: any = { scope: { level: form.level, ref_id: form.level === "store" ? form.ref_id : (form.ref_id || null) },
        metric: form.metric, method: form.method, value: parseFloat(form.value),
        reason_category: form.reason_category, explanation: form.explanation, exceptional_ack: true };
      if (form.effective_from) body.effective_from = form.effective_from;
      if (form.effective_to) body.effective_to = form.effective_to;
      await api.post("/forecast-adjustments", body); toast.success("Adjustment drafted"); setShowCreate(false); load();
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const act = async (id: string, action: string, body?: any) => {
    try {
      if (action === "preview") { const r = await api.post(`/forecast-adjustments/${id}/preview`); setPreview(r.data); return; }
      await api.post(`/forecast-adjustments/${id}/${action}`, body || {});
      toast.success(tl("Adjustment {action}", { action })); load();
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (adjustments === null) return <LoadingBlock />;
  return (
    (<div data-testid="forecast-adjustments-page" className="max-w-full overflow-x-hidden">
      <PageHeader title="g3.adjustments" subtitle="g3.subtitle"
        actions={
          <div className="flex gap-2">
            {canReport && <Button size="sm" variant="outline" onClick={() => setShowReport(true)} data-testid="report-exception-btn"><Megaphone size={14} className="mr-1" />{t("g3.report_exception")}</Button>}
            {canApprove && <Button size="sm" onClick={() => setShowCreate(true)} data-testid="new-adjustment-btn"><TrendingDown size={14} className="mr-1" />{t("g3.new_adjustment")}</Button>}
          </div>
        } />
      {canApprove && reports.filter((r) => r.status === "open").length > 0 && (
        <Card className="mb-4 p-4" data-testid="pending-reports">
          <p className="mb-2 text-sm font-medium">{t("g3.pending_requests")}</p>
          <div className="space-y-2">
            {reports.filter((r) => r.status === "open").map((r) => (
              <div key={r.report_id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-2 text-xs" data-testid={`report-${r.report_id}`}>
                <div className="min-w-0"><StatusPill status="warn">{stat(r.category)}</StatusPill> <span className="text-muted-foreground">{r.description}</span></div>
                <Button size="sm" variant="outline" onClick={() => act(r.report_id, "resolve", { resolution: "closed_unverified", note: "reviewed" }).then?.(load) as any || act(r.report_id, "resolve", { resolution: "closed_unverified" })} data-testid={`report-close-${r.report_id}`}>{t("g3.close_no_change")}</Button>
              </div>
            ))}
          </div>
        </Card>
      )}
      {adjustments.length === 0 ? (
        <EmptyState title="g3.empty_title" description="g3.empty_desc" icon={<TrendingDown size={24} />} />
      ) : (
        <div className="space-y-3">
          {adjustments.map((a) => (
            <Card key={a.adjustment_id} className="p-4" data-testid={`adjustment-${a.adjustment_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusPill status={STATUS_TONE[a.status] || "info"} testId={`adj-status-${a.adjustment_id}`}>{t(`g3.status.${a.status}`)}</StatusPill>
                    {a.status === "published" && <StatusPill status="warn">{t("g3.adjusted_badge")}</StatusPill>}
                    <span className="text-sm font-medium">{a.metric} · {a.method === "percent" ? `${a.value}%` : a.method === "delta" ? `${a.value}` : `= ${a.value}`}</span>
                    <span className="text-xs text-muted-foreground">v{a.version_no} · {a.scope.level}</span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{a.reason_category} · {t("g3.effective")} {a.effective_from}{a.effective_to ? `→${a.effective_to}` : ""}</p>
                  <p className="mt-0.5 text-xs">{a.explanation}</p>
                  {a.affected_labor?.schedules?.length > 0 && <p className="mt-1 text-[11px] text-status-warn">{t("g3.affected_notice")}</p>}
                </div>
                {canApprove && (
                  <div className="flex flex-wrap gap-1.5">
                    {a.status === "submitted" && <>
                      <Button size="sm" variant="outline" onClick={() => act(a.adjustment_id, "preview")} data-testid={`adj-preview-${a.adjustment_id}`}><Eye size={12} className="mr-1" />{t("g3.preview_impact")}</Button>
                      <Button size="sm" onClick={() => act(a.adjustment_id, "publish")} data-testid={`adj-publish-${a.adjustment_id}`}><CheckCircle2 size={12} className="mr-1" />{t("g3.publish")}</Button>
                    </>}
                    {a.status === "published" && <>
                      <Button size="sm" variant="outline" onClick={() => act(a.adjustment_id, "correct", { value: parseFloat(String(a.value)) / 2, explanation: a.explanation + " (corrected)" })} data-testid={`adj-correct-${a.adjustment_id}`}><RotateCcw size={12} className="mr-1" />{t("g3.correct")}</Button>
                      <Button size="sm" variant="outline" onClick={() => act(a.adjustment_id, "cancel", { note: "cancelled" })} data-testid={`adj-cancel-${a.adjustment_id}`}><Ban size={12} className="mr-1" />{t("g3.cancel")}</Button>
                    </>}
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
      {/* report dialog */}
      <Dialog open={showReport} onOpenChange={setShowReport}>
        <DialogContent data-testid="report-dialog">
          <DialogHeader><DialogTitle>{t("g3.report_exception")}</DialogTitle></DialogHeader>
          <Select value={rform.store_id} onValueChange={(v) => setRform((s: any) => ({ ...s, store_id: v }))}>
            <SelectTrigger data-testid="report-store"><SelectValue placeholder={t("g3.store")} /></SelectTrigger>
            <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name || s.store_number}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={rform.category} onValueChange={(v) => setRform((s: any) => ({ ...s, category: v }))}>
            <SelectTrigger data-testid="report-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tech_outage">{t("g3.reason.tech_outage")}</SelectItem>
              <SelectItem value="abnormal_flow">{t("g3.reason.abnormal_flow")}</SelectItem>
              <SelectItem value="weather">{t("g3.reason.weather")}</SelectItem>
              <SelectItem value="other">{t("g3.reason.other")}</SelectItem>
            </SelectContent>
          </Select>
          <Textarea placeholder={t("g3.explanation")} value={rform.description} onChange={(e) => setRform((s: any) => ({ ...s, description: e.target.value }))} data-testid="report-desc" />
          <DialogFooter><Button disabled={busy} onClick={submitReport} data-testid="report-submit">{t("g3.submit")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {/* create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="create-dialog">
          <DialogHeader><DialogTitle>{t("g3.new_adjustment")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            <Select value={form.level} onValueChange={(v) => setForm((s: any) => ({ ...s, level: v }))}>
              <SelectTrigger data-testid="adj-level"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="store">{t("store")}</SelectItem><SelectItem value="tenant">{t("tenant")}</SelectItem></SelectContent>
            </Select>
            {form.level === "store" && (
              <Select value={form.ref_id} onValueChange={(v) => setForm((s: any) => ({ ...s, ref_id: v }))}>
                <SelectTrigger data-testid="adj-store"><SelectValue placeholder={t("g3.store")} /></SelectTrigger>
                <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name || s.store_number}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <Select value={form.metric} onValueChange={(v) => setForm((s: any) => ({ ...s, metric: v }))}>
              <SelectTrigger data-testid="adj-metric"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="traffic">{t("traffic")}</SelectItem><SelectItem value="sales">{t("sales")}</SelectItem><SelectItem value="transactions">{t("transactions")}</SelectItem></SelectContent>
            </Select>
            <Select value={form.method} onValueChange={(v) => setForm((s: any) => ({ ...s, method: v }))}>
              <SelectTrigger data-testid="adj-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="percent">{t("g3.method.percent")}</SelectItem>
                <SelectItem value="delta">{t("g3.method.delta")}</SelectItem>
                <SelectItem value="absolute">{t("g3.method.absolute")}</SelectItem>
              </SelectContent>
            </Select>
            <Input type="number" value={form.value} onChange={(e) => setForm((s: any) => ({ ...s, value: e.target.value }))} placeholder={t("g3.value")} data-testid="adj-value" />
            <Input type="date" value={form.effective_from} onChange={(e) => setForm((s: any) => ({ ...s, effective_from: e.target.value }))} data-testid="adj-eff-from" />
          </div>
          <Textarea placeholder={t("g3.explanation")} value={form.explanation} onChange={(e) => setForm((s: any) => ({ ...s, explanation: e.target.value }))} data-testid="adj-explanation" />
          <label className="flex items-start gap-2 text-xs"><Checkbox checked={form.exceptional_ack} onCheckedChange={(v) => setForm((s: any) => ({ ...s, exceptional_ack: !!v }))} data-testid="adj-ack" /><span>{t("g3.exceptional_ack")}</span></label>
          <DialogFooter><Button disabled={busy || !form.exceptional_ack} onClick={createAdj} data-testid="adj-create-submit">{t("g3.submit")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {/* preview dialog */}
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent data-testid="preview-dialog">
          <DialogHeader><DialogTitle>{t("g3.preview_impact")}</DialogTitle></DialogHeader>
          {preview && <div className="text-xs">
            <p className="text-muted-foreground">{t("g3.preview_summary", { stores: preview.affected_stores, recipients: preview.recipients_count, schedules: preview.affected_labor?.schedules?.length || 0 })}</p>
            <div className="mt-2 space-y-1">
              {preview.sample_impact?.slice(0, 8).map((s: any) => (
                <div key={s.store_id} className="flex justify-between border-b border-border py-0.5">
                  <span>#{s.store_number}</span><span>{t("g3.baseline")} {s.baseline} → {t("g3.adjusted")} {s.adjusted}</span>
                </div>
              ))}
            </div>
            <p className="mt-2 text-status-warn">{preview.note}</p>
          </div>}
        </DialogContent>
      </Dialog>
    </div>)
  );
};

export default ForecastAdjustments;
